import { getMyWindowIdentifier, asyncMap, toGoLang, convertGetMonitorInfo, Gate, getComponentState, setComponentState, removeComponentState, formGroup, getConfigurationAndCache } from "../common/windowFunctions";
import { logStatus } from "../utils";
export class WindowClientImpl {
    async initialize(testId) {
        if (!this.glue.windows) {
            return;
        }
        this.windowIdentifier = await getMyWindowIdentifier(this.glue);
        const id = testId ?? this.windowIdentifier?.windowName;
        const win = this.glue.windows.findById(id);
        if (!win) {
            if (globalThis.window) {
                this.loggerClient.warn("INVARIANT: WindowClient cannot find window", id);
            }
            return;
        }
        this.gdwin = win;
        const context = await this.gdwin?.getContext();
        const config = await getConfigurationAndCache(this.gdwin) ?? {};
        this.spawnData = context?.["#finsemble-spawnData"] ?? {};
        this.options = {
            customData: {
                ...config?.customProperties,
                ...config?.customProperties?.appd?.hostManifests?.Finsemble,
                ...this.spawnData,
                spawnData: this.spawnData
            }
        };
    }
    constructor(gluePromise, asyncConstructors, loggerClient, testId) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.gate = new Gate();
        const pw = asyncConstructors.addConstructor();
        this.loggerClient.verbose("WindowClient Constructor");
        this.options = {
            customData: {}
        };
        gluePromise.then(async (g) => {
            this.glue = g;
            this.initialize(testId).finally(() => {
                this.loggerClient.verbose("WindowClient Initialized");
                asyncConstructors.finishConstructor(pw);
            });
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "WindowClient",
            method, type, data
        });
    }
    async showAtMousePosition() {
        this.logStatus("showAtMousePosition", "start");
        await this.asyncConstructors.ready();
        const position = await this.glue.displays.getMousePosition();
        const bounds = await this.gdwin.getBounds();
        bounds.top = (position?.y ?? 0) - (bounds.height ?? 0) + 10;
        bounds.left = (position?.x ?? 0) - (bounds.width ?? 0) / 2;
        bounds.right = bounds.left + (bounds.width ?? 0);
        bounds.bottom = bounds.top + (bounds.height ?? 0);
        await this.gdwin.show();
        await this.gdwin.moveResize(bounds);
        await this.gdwin.focus();
        this.logStatus("showAtMousePosition", "return");
    }
    async setWindowTitle(title) {
        this.logStatus("setWindowTitle", "start", { title });
        await this.asyncConstructors.ready();
        await this.gdwin.setTitle(title);
        this.logStatus("setWindowTitle", "return");
    }
    setTaskbarIconOverlay(url) {
        this.logStatus("setTaskbarIconOverlay", "start", { url });
        console.log("setTaskbarIconOverlay() is not implemented");
        this.logStatus("setTaskbarIconOverlay", "return");
    }
    setShape(params, callback) {
        console.log("WindowClient.setShape() is no longer supported.");
    }
    async getComponentState(params, cb) {
        this.logStatus("getComponentState", "start", params);
        await this.asyncConstructors.ready();
        const gdwin = params?.windowName ? this.glue.windows.findById(params.windowName) : this.gdwin;
        const result = await getComponentState(params, gdwin);
        this.logStatus("getComponentState", "return", { err: null, data: result });
        cb?.(result.err, result.data);
        return result;
    }
    async setComponentState(params, cb) {
        return await this.gate.wait(async () => this.setComponentStateInternal(params, cb));
    }
    async setComponentStateInternal(params, cb) {
        this.logStatus("setComponentState", "start", params);
        await this.asyncConstructors.ready();
        const gdwin = params.windowName ? this.glue.windows.findById(params.windowName) : this.gdwin;
        const result = await setComponentState(params, gdwin);
        this.logStatus("setComponentState", "return", result);
        cb?.(result.err, result.data);
        return result;
    }
    async setAlwaysOnTop(alwaysOnTop, cb) {
        this.logStatus("setAlwaysOnTop", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.setOnTop(alwaysOnTop), cb);
        this.logStatus("setAlwaysOnTop", "return", { err });
        return { err };
    }
    async retrieveMonitorDimensions(cb) {
        this.logStatus("retrieveMonitorDimensions", "start");
        await this.asyncConstructors.ready();
        const glueMonitor = await this.gdwin.getDisplay();
        const monitorRect = {
            left: glueMonitor.bounds.left * glueMonitor.scaleFactor,
            top: glueMonitor.bounds.top * glueMonitor.scaleFactor,
            width: glueMonitor.bounds.width,
            height: glueMonitor.bounds.height
        };
        monitorRect.right = monitorRect.left + monitorRect.width;
        monitorRect.bottom = monitorRect.top + monitorRect.height;
        this.logStatus("retrieveMonitorDimensions", "return", monitorRect);
        cb(monitorRect);
    }
    async restore(cb) {
        this.logStatus("restore", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.restore(), cb);
        this.logStatus("restore", "return", { err });
        return { err };
    }
    async removeFromTabCollection({ windowNames }) {
        this.logStatus("removeFromTabCollection", "start", { windowNames });
        await this.asyncConstructors.ready();
        await asyncMap(windowNames, async (winName) => {
            const gdwin = this.glue.windows.findById(winName);
            await gdwin?.detachTab({});
        });
        this.logStatus("removeFromTabCollection", "return");
    }
    async removeComponentState(params, cb) {
        this.logStatus("removeComponentState", "start", params);
        await this.asyncConstructors.ready();
        const gdwin = params.windowName ? this.glue.windows.findById(params.windowName) : this.gdwin;
        const result = await removeComponentState(params, gdwin);
        this.logStatus("removeComponentState", "return", result);
        cb?.(result.err, result.data);
        return result;
    }
    async promoteTab(windowName) {
        this.logStatus("promoteTab", "start", { windowName });
        await this.asyncConstructors.ready();
        const win = this.glue.windows.findById(windowName);
        await win?.activate();
        this.logStatus("promoteTab", "return");
    }
    async minimize(cb) {
        this.logStatus("minimize", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.minimize(), cb);
        this.logStatus("minimize", "return", { err });
        return { err };
    }
    async maximize(cb) {
        this.logStatus("maximize", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.maximize(), cb);
        this.logStatus("maximize", "return", { err });
        return { err };
    }
    getWindowIdentifier() {
        this.logStatus("getWindowIdentifier", "start");
        this.logStatus("getWindowIdentifier", "return", this.windowIdentifier);
        return this.windowIdentifier;
    }
    getWindowBounds() {
        const error = "WindowClient.getWindowBounds() is no longer supported. Please use getBounds()";
        console.log(error);
        throw new Error(error);
    }
    async getWindowTitle(windowName) {
        this.logStatus("getWindowTitle", "start", windowName);
        await this.asyncConstructors.ready();
        const win = windowName ? this.glue.windows.findById(windowName) : this.gdwin;
        const title = win?.getTitle();
        this.logStatus("getWindowTitle", "return", title);
        return title ?? "Unknown Window";
    }
    async getTabCollectionForWindow({ windowName }, cb) {
        this.logStatus("getTabCollectionForWindow", "start", { windowName });
        await this.asyncConstructors.ready();
        const gdwin = this.glue.windows.findById(windowName);
        if (!gdwin) {
            const err = `No window found matching id ${windowName}`;
            this.logStatus("getTabCollectionForWindow", "error", err);
            return { err };
        }
        const data = {
            id: gdwin.tabGroupId,
            windows: gdwin.tabs.map((win) => win.id),
            promotedWindowName: gdwin.tabs.find((win) => win.isTabSelected === true)?.id ?? ""
        };
        this.logStatus("getTabCollectionForWindow", "return", { err: null, data });
        cb?.(null, data);
        return {
            data,
            err: null
        };
    }
    async getTabCollection({ id }, cb) {
        this.logStatus("getTabCollection", "start", { id });
        await this.asyncConstructors.ready();
        const gdwin = await this.findGlueWindowInTabGroup(id);
        if (!gdwin) {
            const err = `No window found in tabGroup id ${id}`;
            this.logStatus("getTabCollection", "error", { err });
            return { err };
        }
        const data = {
            windows: gdwin.tabs.map((win) => win.id),
            promotedWindowName: gdwin.tabs.find((win) => win.isTabSelected === true)?.id
        };
        cb?.(null, data);
        return {
            data,
            err: null
        };
    }
    getSpawnData() {
        this.logStatus("getSpawnData", "start");
        const data = this.spawnData;
        this.logStatus("getSpawnData", "return", data);
        return data;
    }
    async getPromotedTab({ tabCollectionId }, cb) {
        this.logStatus("getPromotedTab", "start", { tabCollectionId });
        await this.asyncConstructors.ready();
        const gdwin = await this.findGlueWindowInTabGroup(tabCollectionId);
        if (!gdwin) {
            const err = `No window found in tabGroup id ${tabCollectionId}`;
            this.logStatus("getPromotedTab", "error", { err });
            return { err };
        }
        const promotedTab = gdwin.tabs.find((win) => win.isTabSelected === true)?.id;
        if (!promotedTab) {
            const err = `No promoted tab found for tab collection ${tabCollectionId}`;
            this.logStatus("getPromotedTab", "error", { err });
            cb?.(err);
            return { err };
        }
        else {
            this.logStatus("getPromotedTab", "return", { err: null, data: promotedTab });
            cb?.(null, promotedTab);
            return { err: null, data: promotedTab };
        }
    }
    async getMonitorInfo(params, cb) {
        await this.asyncConstructors.ready();
        const gdMonitor = await this.gdwin.getDisplay();
        const finsembleMonitorInfo = convertGetMonitorInfo(gdMonitor);
        if (!finsembleMonitorInfo) {
            return {
                err: "Monitor not found. Check application.log"
            };
        }
        return {
            err: null,
            data: finsembleMonitorInfo
        };
    }
    startMovingWindow() {
        console.log("WindowClient.startMovingWindow() is no longer supported");
    }
    stopMovingWindow() {
        console.log("WindowClient.stopMovingWindow() is no longer supported");
    }
    async getBounds(cb) {
        this.logStatus("getBounds", "start");
        await this.asyncConstructors.ready();
        const bounds = await this.gdwin.getBounds();
        this.logStatus("getBounds", "return", { err: null, data: bounds });
        cb?.(null, bounds);
        return {
            err: null,
            data: bounds
        };
    }
    async formGroup() {
        this.logStatus("formGroup", "start");
        await formGroup(this.glue, this.gdwin.id);
        this.logStatus("formGroup", "return");
    }
    async fitToDOM(params, cb) {
        this.logStatus("fitToDOM", "start", params);
        await this.asyncConstructors.ready();
        if (typeof params === "function") {
            cb = params;
            params = null;
        }
        const paddingHeight = params?.padding?.height ?? 0;
        const paddingWidth = params?.padding?.width ?? 0;
        const myWindow = this.gdwin;
        const bounds = await myWindow.getBounds();
        if (!bounds) {
            this.logStatus("fitToDOM", "return");
            cb?.();
            return;
        }
        const display = await myWindow.getDisplay();
        const monitorHeight = display.workArea.height ?? Infinity;
        const monitorWidth = display.workArea.width ?? Infinity;
        const titlebarHeight = Math.max(bounds.height - window.innerHeight, 0);
        const verticalScrollbarWidth = window.innerWidth - document.getElementsByTagName("html")[0].clientWidth;
        const horizontalScrollbarHeight = window.innerHeight - document.getElementsByTagName("html")[0].clientHeight;
        const maybeExtraPixel = horizontalScrollbarHeight ? 1 : 0;
        const calculatedHeight = document.documentElement.offsetHeight + paddingHeight + titlebarHeight + maybeExtraPixel;
        const calculatedWidth = myWindow.isVisible ? document.documentElement.scrollWidth : bounds.width + paddingWidth + verticalScrollbarWidth;
        const newHeight = Math.min(calculatedHeight, params?.maxHeight ?? calculatedHeight, monitorHeight);
        const newWidth = Math.min(calculatedWidth, params?.maxWidth ?? calculatedWidth, monitorWidth);
        if (bounds.width !== newWidth || bounds.height !== newHeight) {
            await myWindow.moveResize({
                left: bounds.left,
                top: bounds.top,
                width: newWidth,
                height: newHeight,
            });
        }
        this.logStatus("fitToDOM", "return");
        cb?.();
    }
    async createTabCollection({ windowNames, promotedTab, }) {
        this.logStatus("createTabCollection", "start", { windowNames, promotedTab });
        await this.asyncConstructors.ready();
        const gdwin = this.glue.windows.findById(windowNames.shift() ?? "");
        await asyncMap(windowNames, async (winName) => {
            const options = {};
            if (winName === promotedTab) {
                options.selected = true;
            }
            await gdwin?.attachTab(winName, options);
        });
        this.logStatus("createTabCollection", "return");
    }
    async close(params, cb) {
        this.logStatus("close", "start");
        if (params) {
            this.loggerClient.system.deprecated("The use of parameters in WindowClient.close() has been deprecated.");
        }
        await this.asyncConstructors.ready();
        const { err } = await toGoLang(() => this.gdwin.close(), cb);
        this.logStatus("close", "return", { err });
        return { err };
    }
    async bringToFront(cb) {
        this.logStatus("bringToFront", "start");
        await this.asyncConstructors.ready();
        const { err: err1 } = await toGoLang(() => this.gdwin.show());
        const { err: err2 } = await toGoLang(() => this.gdwin.focus());
        const err = err1 ?? err2;
        if (err) {
            this.logStatus("bringToFront", "error", err);
        }
        this.logStatus("bringToFront", "return");
        return { err };
    }
    async addToTabCollection({ windowNames, tabCollectionId, position, promotedTab, }) {
        this.logStatus("addToTabCollection", "start", { windowNames, tabCollectionId, position, promotedTab });
        await this.asyncConstructors.ready();
        const gdwin = await this.findGlueWindowInTabGroup(tabCollectionId);
        if (!gdwin) {
            this.logStatus("addToTabCollection", "return");
            return;
        }
        await asyncMap(windowNames, async (winName) => {
            const options = {};
            if (winName === promotedTab) {
                options.selected = true;
            }
            if (position) {
                options.index = position;
                position++;
            }
            await gdwin.attachTab(winName, options);
        });
        this.logStatus("addToTabCollection", "return");
    }
    async findGlueWindowInTabGroup(tabGroupId) {
        await this.asyncConstructors.ready();
        const wins = this.glue.windows.list();
        return (wins.find(win => win?.tabGroupId === tabGroupId));
    }
    addListeners() {
        console.log("WindowClient.addListeners() is no longer supported");
    }
}
